import { useNavigate } from 'react-router-dom';
import StateBadge from './StateBadge';
import EntityLinkButton from './EntityLinkButton';

/** Minimal interface — satisfied by both Listing and InseratSummary */
export interface InseratBlockData {
  id: number;
  title: string;
  platform: string;
  state: string;
  vermieter_id?: number | null;
  vermieter_name?: string | null;
  adresse_id?: number | null;
  adresse_str?: string | null;
  haus_id?: number | null;
  haus_bezeichnung?: string | null;
  einheit_id?: number | null;
  einheit_bezeichnung?: string | null;
}

interface InseratBlockProps {
  inserat: InseratBlockData;
}

export default function InseratBlock({ inserat }: InseratBlockProps) {
  const navigate = useNavigate();

  const hasLinks =
    (inserat.vermieter_id && inserat.vermieter_name) ||
    (inserat.adresse_id && inserat.adresse_str) ||
    (inserat.haus_id && inserat.haus_bezeichnung) ||
    (inserat.einheit_id && inserat.einheit_bezeichnung);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Block header */}
      <div className="px-4 py-3 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-900 truncate">{inserat.title}</span>
          <span className="text-xs text-gray-400 capitalize shrink-0">({inserat.platform})</span>
          <StateBadge state={inserat.state} />
        </div>
        <EntityLinkButton
          label={inserat.title.length > 30 ? inserat.title.slice(0, 30) + '…' : inserat.title}
          onClick={() => navigate(`/inserat/${inserat.id}`)}
          color="blue"
          requireModule="inserate"
        />
      </div>

      {/* Navigation rows — only rendered if at least one linked entity has a name */}
      {hasLinks && (
        <div className="px-4 py-3 flex flex-wrap gap-2">
          {inserat.vermieter_id && inserat.vermieter_name && (
            <EntityLinkButton
              label={inserat.vermieter_name}
              onClick={() => navigate(`/vermieter/${inserat.vermieter_id}`)}
              color="teal"
              requireModule="vermieter"
            />
          )}
          {inserat.adresse_id && inserat.adresse_str && (
            <EntityLinkButton
              label={inserat.adresse_str}
              onClick={() => navigate(`/adressen/${inserat.adresse_id}`)}
              color="green"
              requireModule="adressen"
            />
          )}
          {inserat.haus_id && inserat.haus_bezeichnung && (
            <EntityLinkButton
              label={inserat.haus_bezeichnung}
              onClick={() => navigate(`/haeuser/${inserat.haus_id}`)}
              color="green"
              requireModule="haeuser"
            />
          )}
          {inserat.einheit_id && inserat.einheit_bezeichnung && (
            <EntityLinkButton
              label={inserat.einheit_bezeichnung}
              onClick={() => navigate(`/einheiten/${inserat.einheit_id}`)}
              color="green"
              requireModule="objekte"
            />
          )}
        </div>
      )}
    </div>
  );
}
